import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../lib/api-client";
import { queryKeys } from "../query-keys";
import {
  PresignedUploadResponseSchema,
  ConfirmUploadResponseSchema,
  DeleteMediaResponseSchema,
  type PresignedUploadRequest,
} from "@applicant-reviewer/api";

// ---------------------------------------------------------------------------
// Upload media — three-step: get presigned URL, PUT to S3, confirm upload
// ---------------------------------------------------------------------------

interface UploadMediaVariables {
  request: PresignedUploadRequest;
  file: File;
}

interface UploadMediaResult {
  mediaId: string;
  s3Key: string;
}

async function uploadMedia(variables: UploadMediaVariables): Promise<UploadMediaResult> {
  // Step 1: Get the presigned upload URL from the backend
  const { uploadUrl, mediaId, s3Key } = await apiClient.post(
    "/media/presigned-upload",
    PresignedUploadResponseSchema,
    variables.request,
  );

  // Step 2: PUT the file directly to S3 using the presigned URL
  const s3Response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": variables.file.type },
    body: variables.file,
  });

  if (!s3Response.ok) {
    throw new Error(`S3 upload failed: ${s3Response.statusText}`);
  }

  // Step 3: Confirm the upload so the backend removes the TTL
  await apiClient.post(`/media/${mediaId}/confirm`, ConfirmUploadResponseSchema);

  return { mediaId, s3Key };
}

export function useUploadMedia() {
  return useMutation({
    mutationFn: uploadMedia,
  });
}

// ---------------------------------------------------------------------------
// Delete media — DELETE /media/{mediaId}
// Removes both the S3 object and the DynamoDB record
// ---------------------------------------------------------------------------

export function useDeleteMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mediaId: string) =>
      apiClient.delete(`/media/${mediaId}`, DeleteMediaResponseSchema),
    onSuccess: (_data, mediaId) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.candidateMedia.detail(mediaId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.candidateMedia.all,
      });
    },
  });
}
